import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ApiError, api } from "../api/client";
import { useAuth } from "../context/AuthContext";

interface ActionResult {
  ok: boolean;
  action: string;
  request_id: number;
  status: string;
  needs_amend_form: boolean;
}

export default function LeaveActionPage() {
  const { t } = useTranslation();
  const { employee } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");

  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState<ActionResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rejectNotes, setRejectNotes] = useState("");
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [actionFromToken, setActionFromToken] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError(t("leave.action.invalidLink", "Invalid action link."));
      return;
    }
    try {
      const parts = token.split(".");
      if (parts.length === 3) {
        const payload = JSON.parse(atob(parts[1]));
        setActionFromToken(payload.action || null);
        if (payload.action === "reject") {
          setShowRejectForm(true);
          return;
        }
      }
    } catch {
      // ignore parse errors; server will validate the token
    }
    void executeAction(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const executeAction = async (t: string, notes?: string) => {
    setProcessing(true);
    setError(null);
    try {
      const res = await api.post<ActionResult>("/api/leave/action", {
        token: t,
        notes: notes || null,
      });
      setResult(res);
      if (res.needs_amend_form) {
        navigate(`/leave?tab=team`);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Action failed");
    } finally {
      setProcessing(false);
    }
  };

  const submitReject = async () => {
    if (!rejectNotes.trim()) {
      setError(t("leave.rejectReason"));
      return;
    }
    if (token) {
      await executeAction(token, rejectNotes.trim());
    }
  };

  if (!employee) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-lg text-center">
          <p className="text-sm text-gray-600">
            {t("leave.action.loginRequired", "Please log in to process this leave request.")}
          </p>
          <button
            type="button"
            onClick={() => navigate(`/?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`)}
            className="mt-4 w-full rounded-lg bg-pme-blue py-2.5 text-sm font-semibold text-white"
          >
            {t("leave.action.signIn", "Sign In")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-lg">
        <h1 className="mb-4 text-lg font-semibold text-gray-900">
          {t("leave.action.title", "Leave Request Action")}
        </h1>

        {processing && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-pme-blue border-t-transparent" />
            {t("leave.action.processing", "Processing...")}
          </div>
        )}

        {error && (
          <div className="space-y-3">
            <p className="text-sm text-red-600">{error}</p>
            {showRejectForm && !result && (
              <>
                <label className="block text-sm">
                  <span className="block font-medium text-gray-700">
                    {t("leave.action.rejectReason", "Reason for rejection")} *
                  </span>
                  <textarea
                    value={rejectNotes}
                    onChange={(e) => { setRejectNotes(e.target.value); setError(null); }}
                    rows={3}
                    className="input mt-1 w-full"
                  />
                </label>
                <button
                  type="button"
                  onClick={() => void submitReject()}
                  disabled={processing}
                  className="w-full rounded-lg bg-red-600 py-2.5 text-sm font-semibold text-white disabled:opacity-60"
                >
                  {t("leave.reject")}
                </button>
              </>
            )}
            <button
              type="button"
              onClick={() => navigate("/leave?tab=team")}
              className="w-full rounded-lg border border-gray-300 py-2.5 text-sm"
            >
              {t("leave.action.goToLeave", "Go to Leave Management")}
            </button>
          </div>
        )}

        {showRejectForm && !error && !result && !processing && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              {t("leave.action.enterRejectReason", "Please enter a reason for rejecting this leave request.")}
            </p>
            <label className="block text-sm">
              <span className="block font-medium text-gray-700">
                {t("leave.action.rejectReason", "Reason for rejection")} *
              </span>
              <textarea
                value={rejectNotes}
                onChange={(e) => setRejectNotes(e.target.value)}
                rows={3}
                className="input mt-1 w-full"
              />
            </label>
            <button
              type="button"
              onClick={() => void submitReject()}
              disabled={processing}
              className="w-full rounded-lg bg-red-600 py-2.5 text-sm font-semibold text-white disabled:opacity-60"
            >
              {t("leave.reject")}
            </button>
          </div>
        )}

        {result && !result.needs_amend_form && (
          <div className="space-y-3">
            <div className="rounded-lg bg-green-50 p-3 text-sm text-green-800">
              {actionFromToken === "approve"
                ? t("leave.action.approved", "Leave request has been approved.")
                : t("leave.action.rejected", "Leave request has been rejected.")}
            </div>
            <button
              type="button"
              onClick={() => navigate("/leave?tab=team")}
              className="w-full rounded-lg bg-pme-blue py-2.5 text-sm font-semibold text-white"
            >
              {t("leave.action.goToLeave", "Go to Leave Management")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
